/*
 * Version: 2.0.0 - 2026-10-15 15:25 (UTC+8) - Plaud Tayler — coordinate independent modules and reuse the shared tab engine
 */
// Keep module interactions independent of their template order - Plaud Tayler - 2026-10-15 15:25 - Begin
(function () {
  'use strict';
  if (window.aiRecorderInitialized) return;
  window.aiRecorderInitialized = true;
  const selector = '[data-ai-recorder-section]';
  const roots = () => Array.from(document.querySelectorAll(selector));
  function targetFor(link) {
    const key = link.dataset.recorderLink;
    const owner = link.closest(selector);
    const matches = roots().filter((root) => root.dataset.recorderKey === key);
    // Unique navigation keys disambiguate repeated module groups.
    return matches.find((root) => owner && (owner.compareDocumentPosition(root) & Node.DOCUMENT_POSITION_FOLLOWING)) || matches[0];
  }
  function refreshNavigation() {
    document.querySelectorAll('[data-recorder-link]').forEach((link) => {
      const target = targetFor(link);
      link.hidden = !target;
      if (target) link.setAttribute('href', '#' + target.id);
    });
    roots().forEach((root) => root.aiRecorderRefresh?.());
  }
  function init(root) {
    if (!root || root.dataset.aiRecorderReady === 'true') return;
    root.dataset.aiRecorderReady = 'true';
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const observers = [];
    const marquees = Array.from(root.querySelectorAll('.marquee, .rev-marquee'));
    const pause = root.querySelector('[data-recorder-motion-pause]');
    const nav = root.querySelector('.subnav');
    const host = root.closest('.shopify-section');
    const tabs = root.querySelector('[data-ww-tabs]');
    let spyObserver;
    let frameId = 0;
    let userPaused = false;
    function syncMotion() {
      root.classList.toggle('motion-ready', !reduced.matches && 'IntersectionObserver' in window);
      root.classList.toggle('motion-paused', document.hidden || userPaused);
      if (pause) {
        pause.hidden = reduced.matches || !('IntersectionObserver' in window);
        pause.setAttribute('aria-pressed', String(userPaused));
        pause.textContent = userPaused ? pause.dataset.recorderResumeLabel : pause.dataset.recorderPauseLabel;
      }
    }
    if ('IntersectionObserver' in window) {
      const motionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => entry.target.classList.toggle('is-visible', entry.isIntersecting));
      });
      marquees.forEach((element) => motionObserver.observe(element));
      observers.push(motionObserver);
      if (!reduced.matches && !window.Shopify?.designMode) {
        const revealObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('in');
              revealObserver.unobserve(entry.target);
            }
          });
        }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
        root.querySelectorAll('.rv').forEach((element) => revealObserver.observe(element));
        observers.push(revealObserver);
        root.classList.add('js');
      }
    }
    if (tabs && window.wwTabs) {
      const triggers = window.wwTabs.tabsIn(tabs);
      if (triggers.length) window.wwTabs.activate(tabs, Math.max(0, triggers.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')), 'init');
      tabs.dataset.recorderTabsReady = 'true';
    }
    function syncSticky() {
      frameId = 0;
      if (!nav || !host) return;
      const top = parseFloat(getComputedStyle(host).top) || 0;
      nav.classList.toggle('stuck', host.getBoundingClientRect().top <= top + 1);
    }
    function scheduleSticky() {
      if (!frameId) frameId = requestAnimationFrame(syncSticky);
    }
    function refreshSpy() {
      spyObserver?.disconnect();
      if (!nav || !('IntersectionObserver' in window)) return;
      const links = Array.from(nav.querySelectorAll('[data-recorder-link]:not(.sub-cta)'));
      const targets = new Map(links.filter((link) => !link.hidden).map((link) => [targetFor(link), link]));
      spyObserver = new IntersectionObserver((entries) => {
        const entry = entries.filter((item) => item.isIntersecting).pop();
        if (!entry) return;
        links.forEach((link) => { link.classList.remove('active'); link.removeAttribute('aria-current'); });
        const active = targets.get(entry.target);
        if (!active) return;
        active.classList.add('active');
        active.setAttribute('aria-current', 'location');
        nav.querySelector('.wrap')?.scrollTo({ left: active.offsetLeft - nav.clientWidth / 2 + active.clientWidth / 2, behavior: reduced.matches ? 'auto' : 'smooth' });
      }, { rootMargin: '-40% 0px -50% 0px', threshold: 0 });
      targets.forEach((_, target) => { if (target) spyObserver.observe(target); });
      syncSticky();
    }
    function onClick(event) {
      if (event.target.closest('[data-recorder-motion-pause]')) {
        userPaused = !userPaused;
        syncMotion();
        return;
      }
      const link = event.target.closest('[data-recorder-link]');
      if (link && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
        const target = targetFor(link);
        if (target) {
          event.preventDefault();
          target.scrollIntoView({ behavior: reduced.matches ? 'instant' : 'smooth', block: 'start' });
          target.setAttribute('tabindex', '-1');
          target.focus({ preventScroll: true });
          history.replaceState(null, '', '#' + target.id);
        }
        return;
      }
      const video = event.target.closest('a.ytembed');
      if (!video || !root.contains(video) || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const url = new URL(video.href, window.location.href);
      if (!['www.youtube.com', 'youtube.com', 'youtu.be'].includes(url.hostname)) return;
      const id = url.hostname === 'youtu.be' ? url.pathname.slice(1) : url.searchParams.get('v');
      if (!id || !/^[\w-]{11}$/.test(id)) return;
      event.preventDefault();
      const frame = document.createElement('iframe');
      const container = document.createElement('div');
      frame.src = 'https://www.youtube-nocookie.com/embed/' + id + '?autoplay=1&rel=0&playsinline=1';
      frame.title = video.querySelector('.yt-tt b')?.textContent || video.getAttribute('aria-label') || video.textContent.trim();
      frame.allow = 'autoplay; encrypted-media; picture-in-picture';
      frame.allowFullscreen = true;
      frame.referrerPolicy = 'strict-origin-when-cross-origin';
      frame.className = 'yt-frame';
      container.className = 'ytembed playing';
      container.append(frame);
      video.replaceWith(container);
      frame.focus();
    }
    root.addEventListener('click', onClick);
    document.addEventListener('visibilitychange', syncMotion);
    if (reduced.addEventListener) reduced.addEventListener('change', syncMotion);
    else reduced.addListener(syncMotion);
    if (nav) {
      window.addEventListener('scroll', scheduleSticky, { passive: true });
      window.addEventListener('resize', scheduleSticky);
    }
    root.aiRecorderRefresh = refreshSpy;
    root.aiRecorderCleanup = function () {
      root.removeEventListener('click', onClick);
      document.removeEventListener('visibilitychange', syncMotion);
      if (reduced.removeEventListener) reduced.removeEventListener('change', syncMotion);
      else reduced.removeListener(syncMotion);
      window.removeEventListener('scroll', scheduleSticky);
      window.removeEventListener('resize', scheduleSticky);
      cancelAnimationFrame(frameId);
      spyObserver?.disconnect();
      observers.forEach((observer) => observer.disconnect());
      root.classList.remove('js', 'motion-ready', 'motion-paused');
      delete root.dataset.aiRecorderReady;
      delete root.aiRecorderRefresh;
      delete root.aiRecorderCleanup;
    };
    syncMotion();
  }
  function initWithin(container) {
    if (container.matches?.(selector)) init(container);
    container.querySelectorAll(selector).forEach(init);
    refreshNavigation();
  }
  function onSectionLoad(event) { initWithin(event.target); }
  function onSectionUnload(event) {
    if (event.target.matches?.(selector)) event.target.aiRecorderCleanup?.();
    event.target.querySelectorAll(selector).forEach((root) => root.aiRecorderCleanup?.());
    // Shopify removes the old wrapper after its unload event finishes.
    requestAnimationFrame(refreshNavigation);
  }
  function onBlockSelect(event) {
    const root = event.target.closest('[data-ww-tabs]');
    if (!root || !window.wwTabs) return;
    const tab = event.target.closest('[data-tab-trigger]');
    if (tab) window.wwTabs.activate(root, window.wwTabs.tabsIn(root).indexOf(tab), 'editor');
  }
  document.addEventListener('shopify:section:load', onSectionLoad);
  document.addEventListener('shopify:section:unload', onSectionUnload);
  document.addEventListener('shopify:section:reorder', refreshNavigation);
  document.addEventListener('shopify:block:select', onBlockSelect);
  initWithin(document);
})();
// Keep module interactions independent of their template order - Plaud Tayler - 2026-10-15 15:25 - End
